import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  ForeeTransaction,
} from "@/types/pos/foree-report";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { 
  displayValue, 
  formatToPKR, 
  downloadCSV, 
  downloadPDF, 
  displayDate,
  capitalizeFirstLetter,
  foreeReportMapper,
  formatDate
} from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { useGetForeeReportQuery, useLazyGetForeeReportQuery } from "@/services/transactionApi";
import { DataTableViewOptions } from "./data-table-view-options";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function ForeeReportTable() {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Local state for table-specific functionality
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [pendingFilters, setPendingFilters] = useState<Record<string, any>>({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Build query for table with server-side pagination
  const buildTableQuery = () => {
    const params = new URLSearchParams();
    params.append("limit", String(pageSize));
    params.append("offset", String(currentPage * pageSize));
    params.append("sort_order", sorting[0]?.desc ? "desc" : "asc");
    params.append("sort_key", sorting[0]?.id || "id");

    // Add search term if present
    if (searchTerm) {
      params.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });

    return params.toString();
  };

  // API call for table data
  const { data: tableApiData, isLoading } = useGetForeeReportQuery(
    { query: buildTableQuery(), org_id: orgId },
    { skip: false },
  );

  const [getForeeReportForExport] = useLazyGetForeeReportQuery();
  const { toast } = useToast();

  const reportData = React.useMemo(() => {
    return Array.isArray(tableApiData?.data) ? tableApiData?.data : [];
  }, [tableApiData]);

  // Reset to first page when filters or search changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [filters, searchTerm]);

  const toggleSortOrder = (key: string, order: string) => {
    setSorting([{ id: key, desc: order === "desc" }]);
  };

  const searchCriteria = {
    sort_key: sorting[0]?.id || "id",
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  };

  // Helper function to build export query (all data without pagination)
  const buildExportQuery = () => {
    const exportQuery = new URLSearchParams();
    exportQuery.append("offset", "0");
    exportQuery.append("sort_order", sorting[0]?.desc ? "desc" : "asc");
    exportQuery.append("sort_key", sorting[0]?.id || "id");

    // Add search term if present
    if (searchTerm) {
      exportQuery.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              exportQuery.append(key, String(val));
            }
          });
        } else {
          exportQuery.append(key, String(value));
        }
      }
    });

    return exportQuery.toString();
  };


  const handleExportCSV = async () => {
    if (selectAllRows) {
      try {
        const result = await getForeeReportForExport({
          query: buildExportQuery(),
          org_id: orgId,
        }).unwrap();

        if (result?.data && result.data.length > 0) {
          downloadCSV(
            result.data as any[],
            "all_foree_report.csv",
            foreeReportMapper,
          );
          toast({
            variant: "success",
            title: `Exported ${result.data.length} records successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
          });
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Export failed",
        });
      }
    } else {
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      downloadCSV(selectedRows, "foree_report.csv", foreeReportMapper);
    }
  };

  const handleExportPDF = async (
    orientation: "portrait" | "landscape" = "portrait",
  ) => {
    if (selectAllRows) {
      try {
        const result = await getForeeReportForExport({
          query: buildExportQuery(),
          org_id: orgId,
        }).unwrap();

        if (result?.data && result.data.length > 0) {
          await downloadPDF(
            result as unknown as any,
            `all_foree_report.pdf`,
            "Foree Report - All Records",
            {
              start_date: filters.start_date,
              end_date: filters.end_date,
            },
            orientation,
            "foree-report",
          );
          toast({
            variant: "success",
            title: `PDF exported successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
          });
        }
      } catch (error) {
        console.log({error});
        toast({
          variant: "destructive",
          title: "PDF Export failed",
        });
      }
    } else {
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }

      await downloadPDF(
        {
          data: selectedRows,
          total_counts: tableApiData?.total_counts,
          filtered_counts: selectedRows.length,
        } as unknown as any,
        `selected_foree_report.pdf`,
        "Foree Report - Selected Records",
        {
          start_date: filters.start_date,
          end_date: filters.end_date,
        },
        orientation,
        "foree-report",
      );
      toast({
        variant: "success",
        title: `PDF exported successfully`,
      });
    }
  };

  const columns: ColumnDef<ForeeTransaction>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value: any) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="translate-y-[2px]"
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "receipt_number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="TXN Number"
          sortKey="receipt_number"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium">
          {displayValue(row?.original?.receipt_number)}
        </div>
      ),
    },
    {
      accessorKey: "srb_invoice_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="SRB Number"
          sortKey="srb_invoice_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <div className="text-nowrap whitespace-nowrap">
          {displayValue(row?.original?.srb_invoice_id)}
        </div>
      ),
    },
    {
      accessorKey: "member_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <div className="capitalize">
          {displayValue(row?.original?.member_name)}
        </div>
      ),
    },
    {
      accessorKey: "payment_method",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Payment Method"
          sortable={false}
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const methods = row.original.payments?.map(p => p.payment_method) || [];
        return (
          <div className="flex flex-wrap gap-1">
            {methods.length > 0 ? (
              methods.map((method, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] rounded-full">
                  {capitalizeFirstLetter(method)}
                </span>
              ))
            ) : (
              <span></span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "total",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Amount"
          sortKey="total"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <div className="font-semibold">{formatToPKR(row?.original?.total)}</div>;
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created Date"
          sortKey="created_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <div className="text-sm">
          {displayDate(row?.original?.created_at)}
        </div>
      ),
    },
    {
      accessorKey: "payment_status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Foree Status"
          sortKey="payment_status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const foreePayment = row?.original?.payments?.find(p => p.payment_method === "foree");
        const status = (foreePayment?.payment_status || row?.original?.status) as string;
        
        const colorClass = 
          status?.toLowerCase() === "paid" ? "bg-green-500" :
          status?.toLowerCase() === "processing" ? "bg-amber-500" :
          status?.toLowerCase() === "pending" ? "bg-yellow-500" :
          status?.toLowerCase() === "cancelled" ? "bg-red-500" :
          status?.toLowerCase() === "partially refund" ? "bg-blue-500" :
          status?.toLowerCase() === "failed" ? "bg-red-500" :
          status?.toLowerCase() === "settled" ? "bg-green-500" :
          "bg-gray-400";
        
        return (
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${colorClass}`} />
            <span className="capitalize text-xs font-medium">{displayValue(status)}</span>
          </div>
        );
      },
    },
    {
      id: "settlement_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Settlement Date"
          sortable={false}
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const settlementDate = row?.original?.payments?.find(p => p.payment_method === "foree")?.settlement_date;
        return (
          <div className="text-sm">
            {settlementDate ? displayDate(settlementDate) : ""}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: reportData,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle showing select all notification
  React.useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;

    if (
      allPageRowsSelected &&
      selectedRowCount === totalRowsInCurrentPage &&
      totalRowsInCurrentPage > 0 &&
      !selectAllRows
    ) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [
    table.getIsAllPageRowsSelected(),
    table.getSelectedRowModel().rows.length,
    selectAllRows,
  ]);

  // Reset select all when row selection changes
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  React.useEffect(() => {
    if (selectAllRows && reportData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, reportData, table]);

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date ? formatDate(dates.start_date) : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setPendingFilters(prev => ({
      ...prev,
      start_date: formattedStartDate || undefined,
      end_date: formattedEndDate || undefined,
    }));
  };

  const applyFilters = () => {
    setFilters(pendingFilters);
  };

  const resetFilters = () => {
    setPendingFilters({});
    setFilters({});
    setInputValue("");
    setSearchTerm("");
    setCurrentPage(0);
  };

  const totalRecords = tableApiData?.filtered_counts || 0;

  return (
    <div className="w-full space-y-4">
      <Card>
        <CardHeader className="flex flex-col space-y-0 pb-4">
          <div className="flex flex-row gap-2 w-full items-center">
            {/* Search - fills available space */}
            <div className="flex-1 min-w-0">
              <TableSearchFilter
                setSearchCriteria={() => {}} 
                inputValue={inputValue}
                setInputValue={(val) => {
                  setInputValue(val);
                  setSearchTerm(val);
                }}
              />
            </div>

            {/* DatePicker + Buttons — always one row, icon-only on mobile */}
            <div className="flex flex-row gap-2 flex-shrink-0 items-center">
              <div className="flex-1 sm:flex-none sm:w-48 lg:w-auto">
                <DatePickerWithRange
                  name={"Date Range"}
                  value={{
                    start_date: pendingFilters.start_date ? new Date(pendingFilters.start_date) : undefined,
                    end_date: pendingFilters.end_date ? new Date(pendingFilters.end_date) : undefined,
                  }}
                  onValueChange={handleDateRange}
                  label={"Select date range"}
                  className="w-full h-8"
                />
              </div>

              <Button
                variant={"default"}
                className="bg-primary text-black flex items-center justify-center h-8 w-8 p-0 sm:w-auto sm:px-3 gap-2 flex-shrink-0"
                onClick={applyFilters}
              >
                <i className="fa fa-filter text-sm"></i>
                <span className="hidden sm:inline text-sm">Apply</span>
              </Button>

              <Button
                variant={"outline"}
                className="text-black flex items-center justify-center h-8 w-8 p-0 sm:w-auto sm:px-3 gap-2 flex-shrink-0"
                onClick={resetFilters}
              >
                <i className="fa-solid fa-arrows-rotate text-sm"></i>
                <span className="hidden sm:inline text-sm">Reset</span>
              </Button>

              <DataTableViewOptions
                table={table}
                onExportCSV={handleExportCSV}
                onExportPDF={handleExportPDF}
              />
            </div>
          </div>
        </CardHeader>

        {/* Select All Notification */}
        {(showSelectAllNotification || selectAllRows) && (
          <div className="bg-blue-50 border-y border-blue-100 p-2 px-3 sm:px-6 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-4">
            <div className="text-xs sm:text-sm text-blue-800">
              {selectAllRows ? (
                <>
                  All <strong>{totalRecords}</strong> records are selected.
                </>
              ) : (
                <>
                  All <strong>{table.getSelectedRowModel().rows.length}</strong>{" "}
                  records on this page are selected.
                </>
              )}
            </div>
            {!selectAllRows && (
              <button
                onClick={handleSelectAllRows}
                className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
              >
                Select all {totalRecords} records
              </button>
            )}
            {selectAllRows && (
              <button
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
              >
                Clear selection
              </button>
            )}
          </div>
        )}

        <CardContent>
          <div className="border">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader className="bg-secondary">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        <div className="flex justify-center items-center space-x-2">
                          <div className="size-2 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                          <div className="size-2 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                          <div className="size-2 bg-black rounded-full animate-bounce"></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center text-muted-foreground"
                      >
                        No results found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>

          <Pagination
            limit={pageSize}
            offset={currentPage * pageSize}
            totalItems={totalRecords}
            onLimitChange={(val) => {
              setPageSize(val);
              setCurrentPage(0);
            }}
            onNextPage={() => setCurrentPage((prev) => prev + 1)}
            onPrevPage={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
            onFirstPage={() => setCurrentPage(0)}
            onLastPage={() =>
              setCurrentPage(Math.floor((totalRecords - 1) / pageSize))
            }
            isLastPage={currentPage >= Math.floor((totalRecords - 1) / pageSize)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
